import { useCallback } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import type { AppraisalGraphQuery } from '@graphql/__generated__/graphql';

export const APPRAISAL_GRAPH_QUERY = gql(/* GraphQL */ `
  query AppraisalGraph($appraisalId: uuid!) {
    appraisals_by_pk(id: $appraisalId) {
      ...AppraisalDetails
    }
  }
`);

export const LEAD_APPRAISAL_DETAILS_FRAGMENT = gql(/* GraphQL */ `
  fragment AppraisalDetails on appraisals {
    id
    min
    max
    value
    updated_at
    valuations {
      id
      min
      value
      max
      model
      valuation_model {
        name
        short_name
      }
    }
  }
`);

export const GENERATE_APPRAISAL = gql(/* GraphQL */ `
  mutation Appraise($property_id: String!) {
    value_property(property_id: $property_id) {
      appraisal {
        ...AppraisalDetails
      }
    }
  }
`);

type UsePropertyAppraisalReturn = {
  appraiseProperty: (
    property_id: string,
  ) => Promise<AppraisalGraphQuery['appraisals_by_pk']>;
  appraisalData: AppraisalGraphQuery['appraisals_by_pk'];
  queryLoading: boolean;
  mutationLoading: boolean;
  queryError: ErrorLike | undefined;
  mutationError: ErrorLike | undefined;
};

export const usePropertyAppraisal = (
  latestAppraisalId?: string,
): UsePropertyAppraisalReturn => {
  const {
    loading: queryLoading,
    data: queryData,
    error: queryError,
  } = useQuery(APPRAISAL_GRAPH_QUERY, {
    variables: { appraisalId: latestAppraisalId || '' },
    skip: !latestAppraisalId,
  });

  const [appraise, { loading: mutationLoading, error: mutationError }] =
    useMutation(GENERATE_APPRAISAL, {
      update(cache, { data: response }) {
        const newAppraisalData = response?.value_property?.appraisal;
        if (newAppraisalData == null) {
          return;
        }

        cache.writeQuery<AppraisalGraphQuery>({
          query: APPRAISAL_GRAPH_QUERY,
          variables: {
            appraisalId: newAppraisalData.id,
          },
          data: {
            appraisals_by_pk: newAppraisalData,
          },
        });
      },
    });

  const appraiseProperty = useCallback(
    async (property_id: string) => {
      try {
        const { data } = await appraise({ variables: { property_id } });
        return data?.value_property?.appraisal ?? null;
      } catch (error) {
        console.error('Error in appraiseProperty:', error);
        throw error;
      }
    },
    [appraise],
  );

  return {
    appraiseProperty,
    appraisalData: queryData?.appraisals_by_pk,
    queryLoading,
    mutationLoading,
    queryError,
    mutationError,
  };
};
